import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import type { ScrollableTreeRef } from 'components/WorkspaceEditor/types'
import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { useScrollToNewContentSelector } from '../ScrollToNewContentStore'
import { SCROLL_DELAY, SCROLL_OFFSET } from '../constants'
import {
    findContentElement,
    findTreeNodePath,
    highlightElement,
    isElementVisible,
} from '../utils'

/**
 * Automatically scrolls to new content in the workspace editor.
 * Expands tree nodes, scrolls to element, highlights and focuses it.
 */
export const useScrollToNewContent = (
    treeRef: MutableRefObject<ScrollableTreeRef>
) => {
    const { treeData } = useShallowSelector((state) => ({
        treeData: state.treeData,
    }))
    const activeContentId = useScrollToNewContentSelector(
        (state) => state.activeContentId
    )

    const timeoutRef = useRef<NodeJS.Timeout>()

    /**
     * Scrolls to content and highlights it
     */
    const scrollAndHighlight = useCallback(
        (contentId: string) => {
            // Scrolls tree to target node, returns success status
            const scrollToNode = () => {
                const nodeInfo = findTreeNodePath(treeData, contentId)
                if (nodeInfo && treeRef.current?.scrollTo) {
                    treeRef.current.scrollTo({
                        key: nodeInfo.nodeKey,
                        align: 'top',
                        offset: SCROLL_OFFSET,
                    })
                    return true
                }
                return false
            }

            // Highlights element after scroll animation completes
            const highlightAfterDelay = () => {
                timeoutRef.current = setTimeout(() => {
                    const element = findContentElement(contentId)
                    if (element) {
                        highlightElement(element)
                    }
                }, SCROLL_DELAY)
            }

            // Clear any pending operations
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }

            requestAnimationFrame(() => {
                // Check if element is already visible
                const element = findContentElement(contentId)
                if (element && isElementVisible(element)) {
                    highlightElement(element)
                    return
                }

                // Element not visible in viewport — initiate scroll via treeRef
                if (scrollToNode()) {
                    highlightAfterDelay()
                }
            })
        },
        [treeData]
    )

    useEffect(() => {
        if (activeContentId) {
            scrollAndHighlight(activeContentId)
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [activeContentId, scrollAndHighlight])
}
